import { useState, useCallback, useEffect } from 'react';
import { useLocalStorage } from './useLocalStorage';
import { useApi } from './useApi';
import { API_ENDPOINTS } from '../constants';

// API レスポンス型定義
interface AuthResponse {
  user?: User;
  token?: string;
  access?: string;
  access_token?: string;
}

interface TokenResponse {
  access_token?: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  organization?: string;
  is_staff?: boolean;
  is_superuser?: boolean;
  subscription?: {
    plan: string;
    isActive: boolean;
  };
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
}

interface LoginCredentials {
  username: string;
  password: string;
}

interface RegisterData {
  username: string;
  email: string;
  password: string;
}

/**
 * 認証管理用カスタムフック
 * ログイン、ログアウト、ユーザー状態管理を提供
 */
export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    loading: true,
  });
  const [, setForceRender] = useState(0);

  const [token, setToken, removeToken] = useLocalStorage<string | null>('authToken', null);
  const { execute: apiCall } = useApi();

  // トークンの有効性確認
  const validateToken = useCallback(async () => {
    if (!token) {
      if (process.env.NODE_ENV === 'development') {
        console.log('No token found, setting unauthenticated state');
      }
      setAuthState({ user: null, isAuthenticated: false, loading: false });
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('Validating token:', '[TOKEN_HIDDEN]');
    }
    try {
      const userData = await apiCall(API_ENDPOINTS.USER_PROFILE, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (process.env.NODE_ENV === 'development') {
        // セキュリティ: ユーザー情報を安全に表示（個人情報をマスキング）
        const safeUserData = userData ? {
          id: (userData as any).id,
          username: '[USERNAME_HIDDEN]',
          email: '[EMAIL_HIDDEN]',
          hasData: true
        } : null;
        if (process.env.NODE_ENV === 'development') {
          console.log('Token validation response (safe):', safeUserData);
        }
      }

      if (userData && (userData as User).id) {
        // APIは直接ユーザー情報を返す（userData.userではない）
        setAuthState({
          user: userData as User,
          isAuthenticated: true,
          loading: false,
        });
        if (process.env.NODE_ENV === 'development') {
          console.log('Token validation successful, user authenticated');
        }
      } else {
        // トークンが無効な場合
        if (process.env.NODE_ENV === 'development') {
          console.log('Invalid token response, removing token');
        }
        removeToken();
        setAuthState({ user: null, isAuthenticated: false, loading: false });
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      removeToken();
      setAuthState({ user: null, isAuthenticated: false, loading: false });
    }
  }, [token, apiCall, removeToken]);

  // ログイン
  const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    setAuthState(prev => ({ ...prev, loading: true }));

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Calling login API with username:', credentials.username);
      }
      const response = await apiCall(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        body: JSON.stringify(credentials),
      });
      if (process.env.NODE_ENV === 'development') {
        console.log('Login API response received:', response ? 'Success' : 'Failed');
      }

      // トークンとユーザー情報を取得
      const authResponse = response as AuthResponse;
      if (response && authResponse.token && authResponse.user) {
        setToken(authResponse.token);
        setAuthState({
          user: authResponse.user,
          isAuthenticated: true,
          loading: false,
        });
        // 強制的に再レンダリングを促す
        setForceRender(prev => prev + 1);
        // 即座にトークン検証を実行してAuthButtonsなど他のコンポーネントに状態を反映
        setTimeout(() => validateToken(), 50);
        return true;
      }
      if (process.env.NODE_ENV === 'development') {
        console.log('Login failed: Invalid response format');
      }
      setAuthState(prev => ({ ...prev, loading: false }));
      return false;
    } catch (error) {
      console.error('Login failed:', error);
      setAuthState(prev => ({ ...prev, loading: false }));
      // エラーをthrowしないで、falseを返すことで呼び出し側で適切にハンドリング
      return false;
    }
  }, [apiCall, setToken, validateToken]);

  // ユーザー登録
  const register = useCallback(async (userData: RegisterData): Promise<boolean> => {
    setAuthState(prev => ({ ...prev, loading: true }));

    try {
      const response = await apiCall(API_ENDPOINTS.REGISTER, {
        method: 'POST',
        body: JSON.stringify(userData),
      });

      // レスポンス形式の違いに対応（access または token）
      const authResponse = response as AuthResponse;
      const accessToken = authResponse?.access || authResponse?.token;
      if (response && accessToken) {
        setToken(accessToken);
        setAuthState({
          user: authResponse.user!,
          isAuthenticated: true,
          loading: false,
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Registration failed:', error);
      setAuthState(prev => ({ ...prev, loading: false }));
      return false;
    }
  }, [apiCall, setToken]);

  // ログアウト
  const logout = useCallback(async () => {
    try {
      if (token) {
        await apiCall(API_ENDPOINTS.LOGOUT, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      removeToken();
      setAuthState({ user: null, isAuthenticated: false, loading: false });
    }
  }, [token, apiCall, removeToken]);

  // 初期化時のトークン検証
  useEffect(() => {
    validateToken();
  }, [validateToken]);

  // 管理者権限チェック
  const isAdmin = useCallback(() => {
    return authState.user?.is_staff || authState.user?.is_superuser || false;
  }, [authState.user]);

  // 3D Analyzer用JWT取得機能を追加
  const get3DAnalyzerToken = useCallback(async (): Promise<string | null> => {
    if (!authState.isAuthenticated || !authState.user) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ 認証されていないユーザーです');
      }
      return null;
    }

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 3D Analyzerトークン取得開始');
      }
      
      // SPLICEの認証トークンを取得
      const rawToken = localStorage.getItem('authToken');
      if (!rawToken) {
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ SPLICEトークンが見つかりません');
        }
        return null;
      }
      
      // JSON文字列からトークンを抽出（二重クォート対策）
      const spliceToken = rawToken.startsWith('"') && rawToken.endsWith('"') 
        ? rawToken.slice(1, -1) 
        : rawToken;

      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${spliceToken}`
        }
      };
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📡 3D Analyzerトークン取得APIコール:', API_ENDPOINTS.GET_3D_ANALYZER_TOKEN);
      }
      
      const response = await apiCall(API_ENDPOINTS.GET_3D_ANALYZER_TOKEN, requestOptions);

      if (process.env.NODE_ENV === 'development') {
        console.log('📋 3D Analyzerトークンレスポンス受信完了');
      }

      const tokenResponse = response as TokenResponse;
      if (tokenResponse?.access_token) {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ 3D Analyzerトークン取得成功');
        }
        return tokenResponse.access_token;
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ レスポンスにaccess_tokenが含まれていません');
        }
        return null;
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ 3D Analyzerトークン取得エラー:', error);
      }
      return null;
    }
  }, [authState.isAuthenticated, authState.user, apiCall]);

  // 3D Analyzerアプリへの認証付きアクセス
  const access3DAnalyzer = useCallback(async (): Promise<boolean> => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🚀 access3DAnalyzer開始');
      }
      
      // 常に新しいトークンを取得（キャッシュされた古いトークンを避ける）
      const token = await get3DAnalyzerToken();
      if (!token) {
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ 3D Analyzerトークンの取得に失敗');
        }
        alert('認証に失敗しました。再ログインしてください。');
        return false;
      }

      // 3D AnalyzerのURL（Gateway統一アーキテクチャ対応）
      const envUrl = process.env.REACT_APP_3D_ANALYZER_URL;
      const analyzerUrl = envUrl || 'http://3danalyzer.localhost';
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔗 3D Analyzer URL設定確認完了');
      }
      
      // JWTをクエリパラメータで渡して新しいタブで開く
      const redirectUrl = `${analyzerUrl}?token=${token}`;
      
      window.open(redirectUrl, '_blank');
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ 3D Analyzer アクセス完了');
      }
      
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ access3DAnalyzer エラー:', error);
      }
      alert('3D Analyzerへのアクセスに失敗しました。');
      return false;
    }
  }, [get3DAnalyzerToken]);

  return {
    user: authState.user,
    isAuthenticated: authState.isAuthenticated,
    loading: authState.loading,
    isAdmin,
    login,
    register,
    logout,
    validateToken,
    // 3D Analyzer関連を追加
    get3DAnalyzerToken,
    access3DAnalyzer
  };
}; 